import { Show, Button } from 'react-admin';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Chip, Alert, Grid, IconButton } from '@mui/material';
import type { ChipColor, ProgressColor } from '@/types/chip';
import type { TESVendorRecord } from '@/types/entities';
import { Close, Warning, Business, CheckCircle, Schedule, Assignment, Balance } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { STATUS_COLORS } from './constants';

const TESVendorShowContent = ({ record }: { record: TESVendorRecord | undefined }) => {
  const navigate = useNavigate();
  if (!record) return null;

    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <IconButton onClick={() => navigate('/tesVendors')} size="small" sx={{ color: 'text.secondary' }}>
            <Close />
          </IconButton>
        </Box>
        <PageDescription entity="TES Vendor" description="A temporary employment services vendor providing contract workers." icon={<Business />} />
        {record.status === 'Blacklisted' && (
          <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>Vendor is blacklisted — no new assignments permitted</Alert>
        )}
        {record.status === 'Suspended' && (
          <Alert severity="warning" sx={{ mb: 2 }} icon={<Schedule />}>Vendor is suspended — pending review</Alert>
        )}
        {record.status === 'Active' && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>Vendor is active and in good standing</Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Vendor Name', value: record.vendorName, bold: true },
            { label: 'Registration #', value: record.registrationNumber },
            { label: 'Compliance Status', value: record.complianceStatus },
            { label: 'Status', value: record.status, chip: true },
            { label: 'Contact Person', value: record.legalEntity?.companyName },
            { label: 'Email', value: record.legalEntity?.email },
            { label: 'Phone', value: record.legalEntity?.phone },
          ].map(f => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                {f.chip ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={f.value} size="small" color={STATUS_COLORS[record.status] as ChipColor} />
                  </Box>
                ) : (
                  <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5 }}>{f.value ?? '—'}</Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Vendor Information</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  { label: 'Address', value: record.address ? `${record.address.street}, ${record.address.city}` : '—' },
                  { label: 'Province', value: record.address?.province },
                  { label: 'Contract Terms', value: record.contractTerms?.paymentTermsDays ? `${record.contractTerms.paymentTermsDays} days` : '—' },
                  { label: 'COIDA Certificate', value: record.complianceDocuments?.COIDACertificate ? 'Yes' : 'No' },
                  { label: 'Tax Clearance', value: record.complianceDocuments?.taxClearance ? 'Yes' : 'No' },
                ].map((f, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">{f.label}</Typography>
                    <Typography variant="body2" fontWeight={500}>{f.value ?? '—'}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Quick Actions</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button component={Link} to="/tes-assignments" variant="outlined" size="small" startIcon={<Assignment />}>
                  View Assignments
                </Button>
                <Button component={Link} to="/deeming-alerts" variant="outlined" size="small" startIcon={<Warning />}>
                  View Deeming Alerts
                </Button>
                <Button component={Link} to="/liability-records" variant="outlined" size="small" startIcon={<Balance />}>
                  View Liability Records
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
};

export const TESVendorShow = () => (
  <Show render={(p) => <TESVendorShowContent {...p} />} />
);
